import React, { useState, useEffect } from "react";
import { Save, RefreshCw, AlertCircle, Check, FileText, Info, X, Trash2 } from "lucide-react";
import api from "../services/api";

/**
 * LLM Prompts Admin Page
 * Manages the two main LLM prompts for the 2-step architecture:
 * 1. Planner Prompt (LLM #1) - Decides button template or lookup mode
 * 2. Final Responder Prompt (LLM #2) - Generates the final blocks response
 */
const LLMPrompts = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [editText, setEditText] = useState("");

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  const fetchPrompts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get("/api/admin/llm-prompts");
      setPrompts(response.data.prompts || []);
    } catch (error) {
      console.error("Error fetching LLM prompts:", error);
      setError("Failed to load LLM prompts");
      showToast("Failed to load LLM prompts", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPrompts();
  }, []);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handleEdit = (prompt) => {
    setEditingPrompt(prompt);
    setEditText(prompt.prompt_template);
  };

  const handleCancelEdit = () => {
    setEditingPrompt(null);
    setEditText("");
  };

  const handleSave = async () => {
    if (!editingPrompt) return;

    try {
      setIsSaving(true);
      await api.put(`/api/admin/llm-prompts/${editingPrompt.id}`, {
        prompt_template: editText,
      });

      // Clear cache
      await api.post("/api/admin/llm-prompts/cache/clear");

      showToast("Prompt updated successfully!", "success");
      setEditingPrompt(null);
      setEditText("");
      await fetchPrompts();
    } catch (error) {
      console.error("Error saving prompt:", error);
      showToast("Failed to save prompt", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const getPromptDescription = (key) => {
    switch (key) {
      case "planner":
        return "LLM #1: Analyzes user message and decides whether to use a button template or execute database lookups (items, stores, website content).";
      case "final_responder":
        return "LLM #2: Generates the final blocks-based response using chat history, planner decision, and lookup results.";
      default:
        return "";
    }
  };

  const getPromptIcon = (key) => {
    return key === "planner" ? "🎯" : "🤖";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-red-800">Error</h3>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Sort prompts: planner first, then final_responder
  const sortedPrompts = [...prompts].sort((a, b) => {
    const order = { planner: 1, final_responder: 2 };
    return (order[a.key] || 999) - (order[b.key] || 999);
  });

  return (
    <div className="max-w-7xl mx-auto">
      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50 ${
            toast.type === "success" ? "bg-green-600 text-white" : "bg-red-600 text-white"
          }`}
        >
          {toast.type === "success" ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">LLM Prompts</h1>
            <p className="text-gray-600">
              Manage the two main prompts used in the 2-step LLM architecture. Changes take effect
              immediately after saving and clearing cache.
            </p>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-semibold mb-1">2-Step LLM Architecture</p>
          <p>
            <strong>Step 1:</strong> Planner analyzes the message and decides what data is needed.
            <br />
            <strong>Step 2:</strong> Final Responder generates the user-facing response with the
            retrieved data.
          </p>
        </div>
      </div>

      {/* Prompts List */}
      <div className="space-y-6">
        {sortedPrompts.map((prompt) => (
          <div
            key={prompt.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            {/* Prompt Header */}
            <div className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="text-4xl">{getPromptIcon(prompt.key)}</div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{prompt.name}</h3>
                    <p className="text-sm text-gray-600 mb-2">{getPromptDescription(prompt.key)}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <span className="font-semibold">Key:</span> {prompt.key}
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="font-semibold">Status:</span>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            prompt.active
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {prompt.active ? "Active" : "Inactive"}
                        </span>
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="font-semibold">Last Updated:</span>
                        {new Date(prompt.updated_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
                {!editingPrompt && (
                  <button
                    onClick={() => handleEdit(prompt)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    Edit Prompt
                  </button>
                )}
              </div>
            </div>

            {/* Prompt Content */}
            <div className="p-6">
              {editingPrompt?.id === prompt.id ? (
                // Edit Mode
                <div>
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="w-full h-96 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    placeholder="Enter prompt template..."
                  />
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-gray-600">
                      Use placeholders like{" "}
                      <code className="px-2 py-1 bg-gray-100 rounded text-xs">
                        {"{"}
                        {"{"} CHAT_HISTORY {"}"}
                        {"}"}
                      </code>{" "}
                      in your prompt
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={handleCancelEdit}
                        disabled={isSaving}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={isSaving || !editText.trim()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                      >
                        {isSaving ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                        {isSaving ? "Saving..." : "Save & Clear Cache"}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // View Mode
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <pre className="whitespace-pre-wrap font-mono text-xs text-gray-800 max-h-64 overflow-y-auto">
                    {prompt.prompt_template}
                  </pre>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* No Prompts Message */}
      {sortedPrompts.length === 0 && !isLoading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Prompts Found</h3>
          <p className="text-gray-600 mb-6">
            LLM prompts need to be seeded in the database before you can use this page.
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left max-w-2xl mx-auto">
            <p className="text-sm font-semibold text-amber-900 mb-2">
              📋 Run this command on the server:
            </p>
            <code className="block bg-amber-100 px-4 py-3 rounded text-sm text-amber-900 font-mono">
              npm run fix:llm-prompts
            </code>
            <p className="text-xs text-amber-700 mt-2">
              This will seed the full planner and final responder prompts into the database.
            </p>
          </div>

          <div className="text-sm text-gray-500 space-y-1">
            <p>Alternative methods:</p>
            <code className="block bg-gray-100 px-3 py-2 rounded text-xs">npm run prisma:seed</code>
            <p className="text-xs">Seeds all database tables including LLM prompts</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LLMPrompts;
